import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ObservationsService } from '../../../services/Observations/observations.service';
import { Router } from '@angular/router';
import { ObservationRefactor } from '../../../models/observations.model';
import { MetaData } from '../../../models/shared.model';
import { TranslocoService } from '@ngneat/transloco';
import { UserService } from '../../../core/user/user.service';
import { ObservationCustomFieldsService } from '../../../services/observation-custom-fields.service';
import { Project360Service } from '../../../modules/p-dashboard/project360.service';

interface DialogData {
  projectId?: string; // optional for global analytics
  status: 'pending' | 'in_progress' | 'completed' | 'closed';
  observationTypes?: string[]; // optional observation type filter
}

@Component({
  selector: 'app-observations-by-status-dialog',
  templateUrl: './observations-by-status-dialog.component.html',
  styleUrls: ['./observations-by-status-dialog.component.scss'],
})
export class ObservationsByStatusDialogComponent implements OnInit, OnDestroy {
  isLoading = false;
  observations: ObservationRefactor[] = [];
  // Simple client-side pagination support
  pageSize = 20;
  pageIndex = 0;
  meta: MetaData | null = null;
  nwc_client: boolean = false;

  // Dynamic columns based on client type and observation types
  get displayedColumns(): string[] {
    const baseColumns = ['id', 'title', 'status', 'assignee'];

    if (!this.nwc_client || !this.observations || this.observations.length === 0) {
      return baseColumns;
    }

    // Check if any observation should display Record No, Sequence No, or Rev#
    const shouldShowRecordNo = this.observations.some((obs) => {
      const type = obs?.type_label || obs?.type;
      return this.observationCustomFields.shouldDisplayRecordNo(type);
    });

    const shouldShowSequenceNo = this.observations.some((obs) => {
      const type = obs?.type_label || obs?.type;
      return this.observationCustomFields.shouldDisplaySequenceNo(type);
    });

    const shouldShowTotalRecordRev = this.observations.some((obs) => {
      const type = obs?.type_label || obs?.type;
      return this.observationCustomFields.shouldDisplayTotalRecordRev(type);
    });

    // Build columns array based on which fields should be shown
    const columns = ['id'];
    if (shouldShowRecordNo) columns.push('record_no');
    if (shouldShowSequenceNo) columns.push('sequence_no');
    if (shouldShowTotalRecordRev) columns.push('rev_no');
    columns.push('title', 'status', 'assignee');

    return columns;
  }

  /**
   * Get the appropriate label for the Rev column header
   * Returns "Total Record Rev#" for MIR/WIR observations
   * Returns "Rev#" for MIR_LOGS/WIR_LOGS observations
   */
  getRevColumnLabel(): string {
    if (!this.observations || this.observations.length === 0) {
      return 'Total Record Rev#';
    }

    // Check if all observations are LOGS types
    const allLogsTypes = this.observations.every((obs) => {
      const type = obs?.type_label || obs?.type;
      return this.observationCustomFields.shouldShowShortRevLabel(type);
    });

    // If all are LOGS types, show short label, otherwise show full label
    return allLogsTypes ? 'Rev#' : 'Total Record Rev#';
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    public dialogRef: MatDialogRef<ObservationsByStatusDialogComponent>,
    private observationsService: ObservationsService,
    private translate: TranslocoService,
    private router: Router,
    private userService: UserService,
    private observationCustomFields: ObservationCustomFieldsService,
    private _project: Project360Service,
  ) {}

  async ngOnInit(): Promise<void> {
    this.nwc_client = await this.userService.isNwcClient();
    this.fetch();
  }

  /**
   * Convert a value to integer for display
   */
  toInteger(value: number): number {
    return +Number(value).toFixed(0) || 0;
  }

  fetch(): void {
    this.isLoading = true;

    // Use refactored endpoint for both project-specific and global contexts
    this.observationsService.resetObservationFilter();
    this.observationsService.per_page = this.pageSize;
    this.observationsService.page = this.pageIndex + 1;
    this.observationsService.sort_by = 'created_at';
    this.observationsService.sort_dir = 'desc';

    // Apply status filter
    this.observationsService._observationFilter.status = this.data.status;

    // Add observation types filter if provided
    if (this.data.observationTypes && this.data.observationTypes.length > 0) {
      this.observationsService._observationFilter.type = this.data.observationTypes;
    }

    // Pass project_id if provided (for project-specific context)
    const projectId = this.data.projectId || undefined;

    this.observationsService.getObservationsRefactored(projectId).subscribe(
      (res) => {
        this.observations = res?.data || [];
        this.meta = (res?.meta_data as MetaData) || null;
        this.isLoading = false;
      },
      () => {
        this.isLoading = false;
      },
    );
  }

  onPageChange(index: number): void {
    this.pageIndex = index;
    this.fetch();
  }

  onMatPage(event: { pageIndex: number; pageSize: number }): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.fetch();
  }

  plain(text: string): string {
    if (!text) return '';
    const div = document.createElement('div');
    div.innerHTML = text;
    return (div.textContent || div.innerText || '').trim();
  }

  getAssignees(row: ObservationRefactor): string[] {
    const assignees = row?.user_assignees || [];
    return assignees.map((user) => user.user_email);
  }

  getAssigneeTooltip(row: ObservationRefactor): string {
    const assignees = this.getAssignees(row);
    if (assignees.length === 0) {
      return '';
    }
    return assignees.join(', ');
  }

  private STATUS_COLORS: Record<string, { hex: string; bg: string }> = {
    pending: { hex: '#fb9a31', bg: 'rgba(251, 154, 49, 0.15)' },
    in_progress: { hex: '#4672dd', bg: 'rgba(70, 114, 221, 0.15)' },
    completed: { hex: '#1eb02f', bg: 'rgba(30, 176, 47, 0.15)' },
    closed: { hex: '#d568c3', bg: 'rgba(213, 104, 195, 0.15)' },
  };

  statusColor(status: string): string {
    const key = (status || '').toLowerCase();
    return this.STATUS_COLORS[key]?.hex || '#9CA3AF';
  }

  statusBg(status: string): string {
    const key = (status || '').toLowerCase();
    return this.STATUS_COLORS[key]?.bg || 'rgba(156, 163, 175, 0.15)';
  }

  openDetails(observation: ObservationRefactor): void {
    if (!observation?.id || !observation?.project_id) return;
    this.dialogRef.close();
    this.router.navigate([
      '/project-d',
      observation.project_id,
      'observations',
      'details',
      observation.id,
    ]);
  }

  ngOnDestroy(): void {
    console.log('ObservationsByStatusDialogComponent destroyed');
  }
}
